import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import Select from 'components/select';
import { useMutation } from 'react-query';
import axios from 'api/request';
import { endPoints } from 'api/endpoints';
import { useRecoilState } from 'recoil';
import { OrderPayload, selectedMembersList, memberForm, costEstimateState } from 'pages/myidm/store';
import { resetCostEstimateDetails } from 'pages/myidm/utils';
import useDebounce from 'pages/myidm/components/hooks/use-debounce';
import { InputInfo } from 'pages/myidm/components/input-info';
import { REQUEST_TYPE } from 'pages/myidm/utils/constant';

const ADGroup = () => {
    const [isTouched, setIsTouched] = React.useState(false);
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [, setMembers] = useRecoilState(selectedMembersList);
    const [form, setForm] = useRecoilState(memberForm);
    const [options, setOptions] = React.useState([]);
    const [inputValue, setInputValue] = React.useState('');
    const [notFound, setNotFound] = React.useState(false);
    const [, setcostEstimate] = useRecoilState(costEstimateState);

    const adGroupsApi = useMutation((searchVal) => axios.get(`${endPoints?.myIdm?.adGroupApi}/${searchVal}`));

    const adGroupDetailsApi = useMutation((adgrp) => axios.get(`${endPoints.myIdm.adGroupDetailsApi}/${adgrp}`));

    const debouncedInputValue = useDebounce(inputValue, 500);

    const setAdGroup = (adGroup) => {
        setOrder({
            ...order,
            adGroup,
            applicationName: null,
            appShortName: null,
            adGroupType: null,
            adGroupOwner: null,
            adGroupTypeDetails: null,
            groupOwner: null,
            groupOwnerDetails: null,
            estimatedCost: resetCostEstimateDetails,
            adGroupMembers: [],
            nonSafeDetailsGpid: [],
            members: [],
        });
        setForm({
            ...form,
            selectedMember: null,
            member: null,
        });
        setMembers([]);
        setcostEstimate(resetCostEstimateDetails);
    };
    React.useEffect(() => {
        if (adGroupDetailsApi?.data?.data) {
            const adGroupData = adGroupDetailsApi?.data?.data?.[0] || {};
            setOrder({
                ...order,
                adGroupType: adGroupDetailsApi?.data?.data?.[0]?.adgroupType,
                adGroupTypeDetails: null,
                groupOwner: null,
                groupOwnerDetails: null,
                estimatedCost: resetCostEstimateDetails,
                adGroupOwner: adGroupData.adgroupOwnerId && adGroupData.adgroupOwnerName ? `${adGroupData.adgroupOwnerId}-${adGroupData.adgroupOwnerName}` : '',
                adGroupOwnerGpid: adGroupData.adgroupOwnerId || '',
            });
        }
    }, [adGroupDetailsApi?.isSuccess]);
    const handleOnSelectChange = (selected) => {
        setNotFound(false);
        setAdGroup(selected.value);
    };
    React.useEffect(() => {
        if (order?.typeOfRequest === REQUEST_TYPE.MODIFY && !order?.adGroupType && order?.adGroup) adGroupDetailsApi?.mutate(order?.adGroup);
    }, [order?.typeOfRequest, order?.adGroupType, order?.adGroup]);

    React.useEffect(() => {
        if (adGroupsApi?.isSuccess && adGroupsApi.data?.data && debouncedInputValue !== '') {
            const adList = adGroupsApi.data?.data?.map((adGroup) => ({
                label: adGroup,
                value: adGroup,
            }));
            setNotFound(false);
            setOptions(adList);
        } else if (
            adGroupsApi?.isSuccess &&
            adGroupsApi?.data?.data === null &&
            adGroupsApi?.data?.messages?.includes('No Data Found') &&
            debouncedInputValue !== ''
        ) {
            setOrder({ ...order, crossDomainUser: null, isUniversalADGroup: false, members: null, adGroupMembers: null });
            setMembers([]);
            setForm({
                ...form,
                selectedMember: null,
                member: null,
            });
            setNotFound(true);
        }
    }, [adGroupsApi?.isSuccess]);

    useEffect(() => {
        if (debouncedInputValue) {
            setOptions([]);
            adGroupsApi.mutate(debouncedInputValue);
        } else {
            setOptions([]);
        }
    }, [debouncedInputValue]);

    React.useEffect(() => {
        if (!order?.adGroup && order?.isTouched) setIsTouched(true);
        else setIsTouched(false);
    }, [order?.adGroup, order?.isTouched]);

    return (
        <>
            <Box display="flex" alignItems="center" gap={1}>
                <Select
                    key="adGroup"
                    label="Active Directory Group *"
                    value={order?.adGroup || null}
                    options={options}
                    handleOnSelect={(value) => handleOnSelectChange(value)}
                    isLoading={adGroupsApi.isLoading}
                    noOptionsText="Type to search"
                    autoComplete
                    includeInputInList
                    filterSelectedOptions
                    fullWidth
                    onInputChange={(event, newInputValue) => {
                        // setIsTouched(false);
                        setInputValue(newInputValue);
                    }}
                    isRequired={isTouched}
                    sx={{ mt: 0, mb: 0 }}
                    customValidate={notFound ? 'validate' : ''}
                    dataTestId="adgrp-auto"
                    onKeyUp={(event) => {
                        if (event.key === 'Backspace' || event.key === 'Delete') {
                            setOrder({ ...order, adGroupMembers: null, members: null });
                            setForm({
                                ...form,
                                selectedMember: null,
                                member: null,
                            });
                            setMembers([]);
                        }
                    }}
                    onBlur={() => {
                        if (options?.length === 0) {
                            setOrder({ ...order, adGroup: null });
                            setNotFound(false);
                        }
                    }}
                />
                <InputInfo title="Type characters for suggestions." />
            </Box>
        </>
    );
};

export default React.memo(ADGroup);
